import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSpinner,
  faExclamationCircle,
  faClipboardList,
  faUpload,
  faDownload,
  faClock,
  faCheckCircle,
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../contexts/AuthContext';
import { MODULE_TYPE_BADGE, MODULE_TYPE_LABELS } from '../utils/http';

interface StudentAssignment {
  assignmentId: string;
  learnershipTitle: string;
  moduleTitle: string | null;
  title: string;
  instructions: string;
  dueDate: string;
  maxMark: number;
  assessmentType: number;
  criteria: string | null;
  attachmentUrl: string | null;
  attachmentFileName: string | null;
  allowResubmission: boolean;
  submissionId: string | null;
  submittedAt: string | null;
  submittedFileName: string | null;
  isLate: boolean;
  status: number;
  grade: number | null;
  feedback: string | null;
  resubmissionAllowed: boolean;
}

const statusLabels: { [key: number]: { label: string; className: string } } = {
  0: { label: 'Not submitted', className: 'bg-gray-100 text-gray-600' },
  1: { label: 'Submitted', className: 'bg-yellow-100 text-yellow-800' },
  2: { label: 'Graded', className: 'bg-green-100 text-green-700' },
  3: { label: 'Resubmission requested', className: 'bg-orange-100 text-orange-700' },
};

export default function AssignmentsPage() {
  const [assignments, setAssignments] = useState<StudentAssignment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submittingId, setSubmittingId] = useState<string | null>(null);
  const { accessToken } = useAuth();

  useEffect(() => {
    fetchAssignments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAssignments = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await fetch('/api/assignments/mine', {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!response.ok) throw new Error('Failed to load assignments');
      setAssignments(await response.json());
    } catch (err) {
      console.error(err);
      setError('Failed to load your assignments. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileSelect = async (assignmentId: string, file: File) => {
    try {
      setSubmittingId(assignmentId);
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`/api/assignments/${assignmentId}/submit`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${accessToken}` },
        body: formData,
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.message || 'Failed to submit assignment');
      }

      await fetchAssignments();
    } catch (err) {
      console.error(err);
      alert(err instanceof Error ? err.message : 'Failed to submit assignment');
    } finally {
      setSubmittingId(null);
    }
  };

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString('en-ZA', { year: 'numeric', month: 'short', day: 'numeric' });

  const canSubmit = (a: StudentAssignment) => a.status === 0 || (a.status === 3 && a.resubmissionAllowed);

  return (
    <>
      <div className="bg-white border-b border-canvas-300 px-8 py-4 sticky top-0 z-10">
        <h1 className="text-2xl font-bold text-brand-950">Assignments</h1>
        <p className="text-sm text-brand-900/60 mt-1">Coursework for your enrolled learnerships</p>
      </div>

      <div className="p-8">
        {isLoading && (
          <div className="flex items-center justify-center py-24">
            <FontAwesomeIcon icon={faSpinner} className="h-10 w-10 text-brand-600 animate-spin" />
          </div>
        )}

        {!isLoading && error && (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-6 text-center">
            <FontAwesomeIcon icon={faExclamationCircle} className="h-8 w-8 text-red-600 mb-3" />
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {!isLoading && !error && assignments.length === 0 && (
          <div className="card-surface p-12 text-center">
            <FontAwesomeIcon icon={faClipboardList} className="h-12 w-12 text-brand-300 mb-4" />
            <h3 className="text-lg font-bold text-brand-950">No assignments yet</h3>
            <p className="text-sm text-brand-900/60 mt-2">Your trainers haven't published any assignments yet.</p>
          </div>
        )}

        {!isLoading && !error && assignments.length > 0 && (
          <div className="space-y-4">
            {assignments.map((a) => {
              const status = statusLabels[a.status] ?? statusLabels[0];
              return (
                <div key={a.assignmentId} className="card-surface p-6">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-xs text-brand-900/50">{a.learnershipTitle}{a.moduleTitle ? ` · ${a.moduleTitle}` : ''}</p>
                      <h3 className="font-bold text-brand-950 mt-1 flex items-center gap-2 flex-wrap">
                        {a.title}
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${MODULE_TYPE_BADGE[a.assessmentType] ?? ''}`}>
                          {MODULE_TYPE_LABELS[a.assessmentType] ?? 'Knowledge'}
                        </span>
                      </h3>
                      <p className="text-sm text-brand-900/70 mt-2 whitespace-pre-wrap">{a.instructions}</p>
                      {a.criteria && (
                        <p className="text-xs text-brand-900/70 mt-3 bg-canvas-50 border border-canvas-200 rounded-lg p-3 whitespace-pre-wrap">
                          <span className="font-semibold">Assessment criteria:</span> {a.criteria}
                        </p>
                      )}
                    </div>
                    <span className={`inline-block w-fit px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${status.className}`}>
                      {status.label}
                    </span>
                  </div>

                  <div className="flex flex-wrap items-center gap-4 mt-4 text-xs text-brand-900/60">
                    <span className="flex items-center gap-1">
                      <FontAwesomeIcon icon={faClock} className="h-3 w-3" />
                      Due {formatDate(a.dueDate)}
                    </span>
                    <span>Max {a.maxMark} marks</span>
                    {a.attachmentUrl && (
                      <a href={a.attachmentUrl} target="_blank" rel="noreferrer" className="text-brand-700 font-semibold flex items-center gap-1">
                        <FontAwesomeIcon icon={faDownload} className="h-3 w-3" />
                        {a.attachmentFileName || 'Download brief'}
                      </a>
                    )}
                  </div>

                  {a.grade != null && (
                    <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-xl">
                      <p className="text-sm font-semibold text-green-800 flex items-center gap-2">
                        <FontAwesomeIcon icon={faCheckCircle} className="h-4 w-4" />
                        Grade: {a.grade}/{a.maxMark}
                      </p>
                      {a.feedback && <p className="text-sm text-green-700 mt-2 whitespace-pre-wrap">{a.feedback}</p>}
                    </div>
                  )}

                  <div className="mt-4 flex items-center gap-3">
                    {a.submittedFileName && (
                      <span className="text-xs text-brand-900/60">Submitted: {a.submittedFileName}</span>
                    )}
                    {canSubmit(a) && (
                      <label className="px-4 py-2 bg-brand-gradient text-white font-semibold rounded-lg hover:shadow-lg transition-all cursor-pointer text-sm flex items-center gap-2">
                        <FontAwesomeIcon
                          icon={submittingId === a.assignmentId ? faSpinner : faUpload}
                          className={`h-3.5 w-3.5 ${submittingId === a.assignmentId ? 'animate-spin' : ''}`}
                        />
                        {a.status === 3 ? 'Resubmit' : 'Submit work'}
                        <input
                          type="file"
                          className="hidden"
                          disabled={submittingId === a.assignmentId}
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) handleFileSelect(a.assignmentId, file);
                          }}
                        />
                      </label>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}
